import { useState, useEffect, useCallback } from "react";
import { Database, MagnifyingGlass, SignOut, User, ShieldCheck, ArrowLeft } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { checkDbStatus, fetchAccessMe } from "@/lib/api";
import { NavLink, useNavigate, Link } from "react-router-dom";
import { useT } from "@/lib/i18n";
import { useBranding } from "@/lib/branding";
import BrandLogo from "@/components/BrandLogo";
import PartsTable from "@/components/PartsTable";
import PartForm from "@/components/PartForm";
import AddPartModal from "@/components/AddPartModal";
import AdminPanel from "@/components/AdminPanel";
import PmScheduleModal from "@/components/PmScheduleModal";
import { CartHeaderButton } from "@/components/CartDrawer";

const CATEGORIES = [
  { key: "compressor", label: "Compressor", productType: "", productTypes: "", nameContains: "", stage: "single" },
  { key: "compressor-he", label: "HE 2-Stage VSD", productType: "", productTypes: "", nameContains: "", stage: "he" },
  { key: "compressor-heh", label: "HEH 2-Stage VSD", productType: "", productTypes: "", nameContains: "", stage: "heh" },
  { key: "air-dryer", label: "Refrigerant Dryer", productType: "Air Dryer", productTypes: "", nameContains: "", stage: "" },
  { key: "heatless", label: "Adsorption Heatless", productType: "Adsorption Type Air Dryer", productTypes: "", nameContains: "Heatless", stage: "" },
  { key: "heated", label: "Adsorption Heated", productType: "Adsorption Type Air Dryer", productTypes: "", nameContains: "Heated", stage: "" },
  { key: "inline-filter", label: "Inline Filter", productType: "",
    productTypes: "PRE Filter,POST Filter,PRE Filter Element,POST Filter Element", nameContains: "", stage: "" },
  // iter114 — Water/Oil Separators sit between Inline Filter and Air Tank
  // per the spec. Same flat US/ACC tabs (no by-class / by-HP buckets) as
  // dryers + filters because they're a single-product-type category.
  // Real warehouse Product Type is "OIL/Water Separator"; user-facing
  // label is "Water/Oil Separators".
  { key: "water-oil-separators", label: "Water/Oil Separators",
    productType: "OIL/Water Separator", productTypes: "", nameContains: "", stage: "" },
  { key: "air-tank", label: "Air Tank", productType: "Tank", productTypes: "", nameContains: "", stage: "" },
  // iter114 — renamed from "Parts Price List" → "Service Kits / Oil"
  // per the user's spec. Filter (in `parts_routes.list_parts`) is:
  //   NOT(Product Name contains 'US_EXT') AND
  //   ( Product Type='Oil Filter Kit'
  //     OR (Product Type='Oil' AND Product Name contains 'Star')
  //     OR Product_CLass='Consumable: Grease' ) AND
  //   In Price List='TRUE'.
  // The PartsTable groups these into 3 buckets: Service Kits,
  // Oil/Lubricant, Grease. Visible to every authenticated user
  // (business staff, dealers, customers); guests don't see it.
  { key: "service-kits-oil", label: "Service Kits / Oil", productType: "", productTypes: "", nameContains: "",
    stage: "parts_only" },
];

// Hide the Parts Price List from anonymous guests only — everyone else
// (US Air staff, ACC staff, dealers, authenticated customers) gets access.
function canSeePartsList(user) {
  if (!user) return false;
  const email = (user.email || "").toLowerCase();
  if (!email || email.endsWith("@local.guest")) return false;
  return true;
}

const TAB_CONFIG = {
  "us-class": { prefix: "US", groupMode: "class", label: "US / By Class" },
  "us-hp":    { prefix: "US", groupMode: "hp",    label: "US / By HP" },
  "acc-class":{ prefix: "ACC", groupMode: "class", label: "ACC / By Class" },
  "acc-hp":   { prefix: "ACC", groupMode: "hp",    label: "ACC / By HP" },
};
// Dryers don't have meaningful HP groupings, so collapse the 4 tabs into 2.
const DRYER_TAB_CONFIG = {
  "us-class": { prefix: "US", groupMode: "class", label: "US" },
  "acc-class":{ prefix: "ACC", groupMode: "class", label: "ACC" },
};

export default function CompressorPage({ user, onLogout, tab }) {
  const t = useT();
  const branding = useBranding();
  const [dbConnected, setDbConnected] = useState(null);
  const [globalSearch, setGlobalSearch] = useState("");
  const [showPartForm, setShowPartForm] = useState(false);
  const [editPart, setEditPart] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [category, setCategory] = useState("compressor");
  const [addPartPreset, setAddPartPreset] = useState(null);
  const [access, setAccess] = useState(null);
  const [pmPart, setPmPart] = useState(null);  // compressor Product_Type for PM modal

  useEffect(() => {
    let mounted = true;
    fetchAccessMe().then((m) => { if (mounted) setAccess(m); }).catch(() => {});
    return () => { mounted = false; };
  }, [user?.email]);

  const cat = CATEGORIES.find((c) => c.key === category) || CATEGORIES[0];
  // Dryer + Inline Filter categories are "non-compressor" and only show US/ACC tabs (no HP grouping).
  // iter114 — parts_only stage (Service Kits / Oil) also uses the
  // simple US/ACC tab set (no by-class / by-HP buckets), since it's a
  // flat spec list and the user explicitly asked for US/ACC categorization
  // only.
  const isFlatCat = Boolean(cat.productType) || Boolean(cat.productTypes) || cat.stage === "parts_only";
  const activeTabConfig = isFlatCat ? DRYER_TAB_CONFIG : TAB_CONFIG;
  // When switching into a dryer category on an HP tab, fall back to the class equivalent.
  const resolvedTab = activeTabConfig[tab] ? tab : (tab?.startsWith("acc") ? "acc-class" : "us-class");
  const cfg = activeTabConfig[resolvedTab] || activeTabConfig["us-class"];
  const navigate = useNavigate();
  useEffect(() => {
    if (isFlatCat && !activeTabConfig[tab]) {
      const next = tab?.startsWith("acc") ? "acc-class" : "us-class";
      navigate(`/${next}`, { replace: true });
    }
  }, [isFlatCat, tab, navigate, activeTabConfig]);

  useEffect(() => {
    checkDbStatus().then((d) => setDbConnected(d.connected)).catch(() => setDbConnected(false));
  }, []);

  const triggerRefresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header data-testid="app-header" className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-slate-200">
        <div className="max-w-[1600px] mx-auto px-2 sm:px-4 md:px-8 h-14 flex items-center gap-1.5 sm:gap-4">
          <button onClick={() => navigate("/")} className="w-8 h-8 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500 shrink-0" title={t("compressor_page.back_home", "Back to Home")}>
            <ArrowLeft size={18} weight="bold" />
          </button>
          <div className="hidden sm:flex items-center gap-2 mr-2 shrink-0">
            <BrandLogo size={22} className="text-blue-700" />
            <h1 className="text-base md:text-lg font-black tracking-tighter font-['Chivo']">{branding.title || t("compressor_page.brand_title", "Compressors R Us")}</h1>
          </div>

          <div data-testid="db-status-indicator" className="flex items-center gap-2 mr-0 sm:mr-2 shrink-0">
            <div className="relative flex items-center justify-center w-3 h-3">
              {dbConnected === true && (
                <><span className="absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-50 animate-ping" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-green-500" /></>
              )}
              {dbConnected === false && <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500" />}
              {dbConnected === null && <span className="relative inline-flex rounded-full h-2 w-2 bg-slate-300 animate-pulse" />}
            </div>
          </div>

          <div className="flex-1 min-w-0 max-w-md relative">
            <MagnifyingGlass size={16} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="global-search-input" placeholder={`Search ${cat.label.toLowerCase()}s...`}
              className="pl-9 h-9 rounded-sm border-slate-300 text-sm focus:ring-2 focus:ring-blue-600 focus:border-transparent"
              value={globalSearch} onChange={(e) => setGlobalSearch(e.target.value)} />
          </div>

          {/* Admin management button */}
          {user?.is_admin && (
            <button data-testid="admin-panel-button" onClick={() => setShowAdminPanel(true)}
              className="w-8 h-8 flex items-center justify-center rounded-sm border border-slate-200 hover:bg-slate-100 text-slate-500 hover:text-blue-700 transition-colors shrink-0" title={t("compressor_page.manage_admins", "Manage Admins")}>
              <ShieldCheck size={16} weight="bold" />
            </button>
          )}

          <CartHeaderButton />

          <div className="flex items-center gap-1 sm:gap-2 sm:ml-2 shrink-0">
            <div className="hidden sm:flex items-center gap-1.5 text-xs text-slate-500">
              <User size={14} weight="bold" />
              <span className="hidden md:inline max-w-[120px] truncate">{user?.email || "Guest"}</span>
              {access?.is_super_admin ? (
                <Link to="/my-access" data-testid="user-role-chip" className="text-[9px] font-bold uppercase tracking-wider text-purple-700 bg-purple-50 border border-purple-200 px-1 py-0.5 rounded-sm hover:bg-purple-100">Super</Link>
              ) : access?.role ? (
                <Link to="/my-access" data-testid="user-role-chip" className="text-[9px] font-bold uppercase tracking-wider text-blue-700 bg-blue-50 border border-blue-200 px-1 py-0.5 rounded-sm hover:bg-blue-100 truncate max-w-[90px]">{access.role}</Link>
              ) : user?.is_admin && (
                <span className="text-[9px] font-bold uppercase tracking-wider text-blue-600 bg-blue-50 border border-blue-200 px-1 py-0.5 rounded-sm">Admin</span>
              )}
            </div>
            <button data-testid="logout-button" onClick={onLogout}
              className="w-7 h-7 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors" title={t("compressor_page.sign_out", "Sign out")}>
              <SignOut size={15} />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto p-3 sm:p-4 md:p-8">
        {/* Category selector */}
        <div className="mb-4 flex flex-wrap items-center gap-2" data-testid="category-selector">
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400 mr-1">Category</span>
          {CATEGORIES.filter((c) => c.key !== "parts" || canSeePartsList(user)).map((c) => (
            <button key={c.key} onClick={() => setCategory(c.key)}
              data-testid={`category-${c.key}`}
              className={`px-3 py-1.5 text-xs font-semibold rounded-sm border transition-colors ${
                category === c.key
                  ? "bg-blue-600 text-white border-blue-600"
                  : "bg-white text-slate-600 border-slate-200 hover:border-blue-300 hover:bg-blue-50"
              }`}>
              {c.label}
            </button>
          ))}
        </div>

        {/* Tabs (2 for dryers, 4 for compressors) */}
        <div className="flex items-center gap-0.5 mb-6 border-b border-slate-200 overflow-x-auto">
          {Object.entries(activeTabConfig).map(([key, { label }]) => (
            <NavLink key={key} to={`/${key}`} data-testid={`tab-${key}`}
              className={`px-4 py-2.5 text-sm font-semibold border-b-2 transition-colors whitespace-nowrap ${
                resolvedTab === key ? "border-blue-700 text-blue-700" : "border-transparent text-slate-500 hover:text-slate-700"
              }`}>
              {label}
            </NavLink>
          ))}
        </div>

        <PartsTable
          search={globalSearch}
          onEdit={user?.is_admin ? (p) => { setEditPart(p); setShowPartForm(true); } : null}
          canAddPart={Boolean(access?.permissions?.includes?.("can_add_part_us") || access?.permissions?.includes?.("can_add_part_acc"))}
          onAddPart={(productClass) => {
            setEditPart(null);
            setAddPartPreset({
              product_class: productClass,
              product_type: cat.productType || "",
              apply_to: cfg.prefix === "ACC" ? "acc" : "us",
            });
          }}
          refreshKey={refreshKey}
          groupMode={cfg.groupMode}
          prefix={cfg.prefix}
          userEmail={user?.email || ""}
          isAdmin={Boolean(user?.is_admin)}
          user={user}
          productType={cat.productType}
          productTypes={cat.productTypes}
          nameContains={cat.nameContains}
          stage={cat.stage || ""}
          onOpenPmSchedule={(p) => setPmPart(p)}
        />
      </main>

      {showPartForm && (
        <PartForm part={editPart} preset={addPartPreset}
          onClose={() => { setShowPartForm(false); setEditPart(null); setAddPartPreset(null); }}
          onSaved={triggerRefresh} />
      )}
      {addPartPreset && !showPartForm && (
        <AddPartModal preset={addPartPreset} userEmail={user?.email || ""}
          onClose={() => setAddPartPreset(null)}
          onSaved={() => { triggerRefresh(); }} />
      )}
      {showAdminPanel && (
        <AdminPanel user={user} onClose={() => setShowAdminPanel(false)} />
      )}
      {pmPart && (
        <PmScheduleModal
          productType={pmPart._ComputedKey || pmPart["_ComputedKey"] || ""}
          displayName={pmPart["Product Name"] || pmPart.Product_Name || ""}
          brand={cfg.prefix === "ACC" ? "ACC" : "US"}
          onClose={() => setPmPart(null)}
        />
      )}
    </div>
  );
}
